/**
 * LYT Communications - Map & Project Configuration
 * Version: 1.2.0
 * Created: 2026-02-02
 * Updated: 2026-02-04
 *
 * Central configuration for the Project Map System including
 * status colors, map icons, billing rates, PM thresholds, and project constants.
 */

// Google Maps API key (public, restricted by HTTP referrer to lytcomm.com)
export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCbZXQimBAuIRXJQNq64VjF94FD35JOvLs';

// Shared Google Maps loader config — single reference prevents useJsApiLoader conflicts across SPA pages
export const GOOGLE_MAPS_LIBS = ['drawing'];
export const GOOGLE_MAPS_VERSION = '3.58';

// Segment status color coding
export const STATUS_COLORS = {
  NOT_STARTED: '#FF4444',
  IN_PROGRESS: '#FFB800',
  COMPLETE: '#4CAF50',
  QC_APPROVED: '#2196F3',
  BLOCKED: '#9E9E9E',
  ISSUE: '#FF9800',
  CRITICAL_PATH: '#2196F3',
};

// Colorblind-safe palette (deuteranopia/protanopia — no red/green)
export const STATUS_COLORS_CB = {
  NOT_STARTED: '#D55E00',   // vermillion (was red)
  IN_PROGRESS: '#F0E442',   // yellow
  COMPLETE: '#009E73',      // teal (was green — distinct from aerial blue)
  QC_APPROVED: '#CC79A7',   // pink/mauve
  BLOCKED: '#9E9E9E',       // grey (unchanged)
  ISSUE: '#E69F00',         // amber/orange
  CRITICAL_PATH: '#CC79A7',
};

// Billing status color coding
export const BILLING_STATUS_COLORS = {
  'Not Billed': '#9E9E9E',
  'Submitted': '#FFB800',
  'Approved': '#2196F3',
  'Paid': '#4CAF50',
};

export const BILLING_STATUS_COLORS_CB = {
  'Not Billed': '#9E9E9E',
  'Submitted': '#F0E442',
  'Approved': '#CC79A7',
  'Paid': '#009E73',
};

// Map marker icons by type
export const MAP_ICONS = {
  HANDHOLE_15x20x12: '📦',
  HANDHOLE_17x30x18: '🔷',
  HANDHOLE_30x48x24: '🔶',
  SPLICE_1X4: '🔍',
  SPLICE_1X8: '🔌',
  SPLICE_F1: '🔵',
  CREW_BORING: '🚧',
  CREW_PULLING: '🚛',
  CREW_SPLICING: '⚡',
  UTILITY_CROSSING: '⚠️',
  ISSUE: '🚨',
};

// Photo requirements by splice type
export const PHOTO_REQUIREMENTS = {
  '1x4': {
    enclosureCount: 7,
    pmPhotoCount: 2, // 1 photo per splitter (not per reading)
    totalPhotos: 9, // 7 enclosure + 2 PM photos
    enclosureTypes: [
      'Basket',
      'Splice tray',
      'Attached strength members',
      'Grommets from inside',
      'Completed enclosure closed',
      'Cables entering enclosure',
      'Enclosure in ground',
    ],
    splitterCount: 2,
    pmReadingCount: 8, // 8 readings total, but only 2 photos
    splitters: [
      { name: 'Splitter A', ports: ['SA1P1', 'SA1P2', 'SA1P3', 'SA1P4'] },
      { name: 'Splitter B', ports: ['SB1P5', 'SB1P6', 'SB1P7', 'SB1P8'] },
    ],
  },
  '1x8': {
    enclosureCount: 8,
    pmReadingCount: 0, // No PM testing at 1x8 locations
    totalPhotos: 8,
    enclosureTypes: [
      'Basket',
      'Splitter tray',
      'Splice tray',
      'Attached strength members',
      'Grommets from inside',
      'Completed enclosure closed',
      'Cables entering enclosure',
      'Enclosure in ground',
    ],
  },
  '2x8': {
    enclosureCount: 8,
    pmReadingCount: 0, // No PM testing at 1x8 hub locations (legacy 2x8 key)
    totalPhotos: 8,
    enclosureTypes: [
      'Basket',
      'Splitter tray',
      'Splice tray',
      'Attached strength members',
      'Grommets from inside',
      'Completed enclosure closed',
      'Cables entering enclosure',
      'Enclosure in ground',
    ],
  },
  'F1': {
    baseCount: 5,
    perTray: 1,
    pmReadingCount: 0,
    baseTypes: [
      'Basket',
      'Strength members and grounds attached',
      'Completed enclosure exterior (closed)',
      'Cable entry with plugs/grommets',
      'Enclosure in handhole',
    ],
    trayPrefix: 'Splice tray',
  },
  'TYCO-D': {
    baseCount: 5,
    perTray: 1,
    pmReadingCount: 0,
    baseTypes: [
      'Basket',
      'Strength members and grounds attached',
      'Completed enclosure exterior (closed)',
      'Cable entry with plugs/grommets',
      'Enclosure in handhole',
    ],
    trayPrefix: 'Splice tray',
  },
};

// Power Meter Thresholds (FTTH Standard)
// These determine pass/fail status for PM readings at 1x4 locations
// TODO: Make adjustable in website settings
export const PM_THRESHOLDS = {
  pass: { min: -25, max: -8 },      // Good signal: -8 to -25 dBm
  warning: { min: -28, max: -25 },  // Marginal: -25 to -28 dBm (investigate)
  failWeak: { max: -28 },           // Too much loss: weaker than -28 dBm
  failStrong: { min: -8 },          // Could overload ONT: stronger than -8 dBm
};

// PM Reading statuses
export const PM_STATUS = {
  PENDING: 'pending',
  NO_LIGHT: 'no_light',  // Fiber not yet lit - timestamp and complete later
  PASS: 'pass',
  WARNING: 'warning',
  FAIL: 'fail',
};

// Helper function to evaluate PM reading status
export function evaluatePMReading(dBm) {
  if (dBm === null || dBm === undefined) return PM_STATUS.PENDING;
  if (dBm > PM_THRESHOLDS.pass.max) return PM_STATUS.FAIL; // Too strong
  if (dBm < PM_THRESHOLDS.failWeak.max) return PM_STATUS.FAIL; // Too weak
  if (dBm < PM_THRESHOLDS.warning.max) return PM_STATUS.WARNING; // Marginal
  return PM_STATUS.PASS;
}

// Workflow phase order
export const WORKFLOW_PHASES = ['boring', 'pulling', 'splicing', 'restoration'];

// Status transitions allowed per phase
export const STATUS_TRANSITIONS = {
  boring: {
    'Not Started': ['In Progress'],
    'In Progress': ['Complete', 'Issue'],
    'Complete': ['QC Approved', 'Issue'],
    'QC Approved': [],
    'Issue': ['In Progress'],
  },
  pulling: {
    'Not Started': ['In Progress'],
    'In Progress': ['Complete', 'Issue'],
    'Complete': ['QC Approved', 'Issue'],
    'QC Approved': [],
    'Issue': ['In Progress'],
  },
  splicing: {
    'Not Started': ['In Progress'],
    'In Progress': ['Complete', 'Issue'],
    'Complete': ['QC Approved', 'Issue'],
    'QC Approved': [],
    'Issue': ['In Progress'],
  },
  restoration: {
    'Not Started': ['In Progress'],
    'In Progress': ['Complete'],
    'Complete': ['QC Approved'],
    'QC Approved': [],
  },
};

// Handhole type mapping
export const HANDHOLE_TYPES = {
  '15x20x12': { label: 'Terminal Box (TB)', size: 'small', icon: MAP_ICONS.HANDHOLE_15x20x12, unitCode: 'UG20' },
  '17x30x18': { label: 'HDPE Handhole (B)', size: 'medium', icon: MAP_ICONS.HANDHOLE_17x30x18, unitCode: 'UG17' },
  '24x36x18': { label: 'HDPE Handhole', size: 'medium', icon: MAP_ICONS.HANDHOLE_17x30x18, unitCode: 'UG18' },
  '24x36x24': { label: 'Fiberglass/Polycrete', size: 'medium', icon: MAP_ICONS.HANDHOLE_17x30x18, unitCode: 'UG11' },
  '30x48x18': { label: 'HDPE Handhole', size: 'large', icon: MAP_ICONS.HANDHOLE_30x48x24, unitCode: 'UG19' },
  '30x48x24': { label: 'HDPE Large Handhole (LHH)', size: 'large', icon: MAP_ICONS.HANDHOLE_30x48x24, unitCode: 'UG27' },
  '30x48x30': { label: 'Fiberglass/Polycrete', size: 'large', icon: MAP_ICONS.HANDHOLE_30x48x24, unitCode: 'UG10' },
};

// Role-based permissions
export const PERMISSIONS = {
  Admin: {
    canViewAllProjects: true,
    canCreateProjects: true,
    canDeleteProjects: true,
    canAssignWork: true,
    canApproveQC: true,
    canGenerateInvoices: true,
    canManageUsers: true,
    canViewRateCard: true,
    canViewOtherContractors: true,
  },
  Contractor: {
    canViewAssignedWork: true,
    canUpdateStatus: true,
    canUploadPhotos: true,
    canReportIssues: true,
    canViewRateCard: false,
    canViewOtherContractors: false,
  },
  Employee: {
    canViewAssignedWork: true,
    canUpdateStatus: true,
    canUploadPhotos: true,
    canReportIssues: true,
    canViewAllProjects: true,
    canViewRateCard: false,
  },
  'QC Inspector': {
    canViewAllWork: true,
    canApproveWork: true,
    canRejectWork: true,
    canViewPhotos: true,
    canAddQCNotes: true,
  },
  Supervisor: {
    canViewAllProjects: true,
    canAssignWork: true,
    canApproveQC: true,
    canManageUsers: false,
    canViewRateCard: true,
    canViewOtherContractors: true,
  },
  Sub: {
    canViewAssignedWork: true,
    canUpdateStatus: true,
    canUploadPhotos: true,
    canReportIssues: true,
    canViewRateCard: false,
    canViewOtherContractors: false,
    canApproveQC: false,
    canGenerateInvoices: false,
    canManageUsers: false,
  },
};

// Mobile breakpoints
export const BREAKPOINTS = {
  MOBILE: 480,
  TABLET: 768,
  DESKTOP: 1024,
  WIDE: 1440,
};

// ============================================================================
// CONTRACTOR rate cards — contractor pay rates ONLY.
// Client rates are SERVER-SIDE ONLY (api/_ratecards.js, served via /api/rates).
// NEVER add client rates or margins here — this file ships in the public JS
// bundle that every visitor (including contractors) downloads.
// Admin pages that need client rates use fetchFullRates() in mapService.js.
// ============================================================================

export const VEXUS_RATES = {
  AE1:        { description: 'Place 6M strand', uom: 'LF', contractor_rate: 0.50 },
  AE2:        { description: 'Lash Cable up to 144F on new strand', uom: 'LF', contractor_rate: 0.60 },
  AE3:        { description: 'Overlash to existing up to 144F', uom: 'LF', contractor_rate: 0.60 },
  'AE3.1':    { description: 'Lash/Overlash larger than 144F', uom: 'LF', contractor_rate: 0.90 },
  AE4:        { description: 'Place Down Guy incl Guy Guard', uom: 'EA', contractor_rate: 14.00 },
  AE5:        { description: 'Place Screw Anchor 6000 lbs', uom: 'EA', contractor_rate: 30.00 },
  AE6:        { description: 'Place Guy Guard', uom: 'EA', contractor_rate: 0.00 },
  AE7:        { description: 'Place 2in Riser Guard', uom: 'EA', contractor_rate: 30.00 },
  AE8:        { description: 'Place ADSS cable', uom: 'LF', contractor_rate: 0.40 },
  AE9L:       { description: 'Cable Extension Arm Long', uom: 'EA', contractor_rate: 50.00 },
  AE9S:       { description: 'Cable Extension Arm Short/Sidewalk', uom: 'EA', contractor_rate: 40.00 },
  AE10:       { description: 'Tree Trimming', uom: 'Span', contractor_rate: 25.00 },
  AE11:       { description: 'Resag cable', uom: 'Span', contractor_rate: 25.00 },
  AE12:       { description: 'Delash/relash', uom: 'LF', contractor_rate: 0.60 },
  AE13:       { description: 'Dead end Pole Transfer', uom: 'EA', contractor_rate: 50.00 },
  AE14:       { description: 'Straight thru Pole Transfer', uom: 'EA', contractor_rate: 35.00 },
  AE15:       { description: 'Bonding aerial strand', uom: 'EA', contractor_rate: 8.00 },
  AE17:       { description: 'Place Aerial Damper Unit', uom: 'EA', contractor_rate: 20.00 },
  AE18:       { description: 'Place Tree/Squirrel Guard', uom: 'LF', contractor_rate: 0.25 },
  AE19:       { description: 'Remobilize temp to permanent pole attachment', uom: 'EA', contractor_rate: 35.00 },
  AE31:       { description: 'Figure 8 cable up to 144F dip transition', uom: 'EA', contractor_rate: 22.00 },
  'AE31.1':   { description: 'Figure 8 cable larger than 144F dip transition', uom: 'LF', contractor_rate: 0.90 },
  FS1:        { description: 'Fusion splice 1 fiber', uom: 'EA', contractor_rate: 13.00 },
  FS2:        { description: 'Ring cut', uom: 'EA', contractor_rate: 180.00 },
  FS3:        { description: 'Test Fiber', uom: 'EA', contractor_rate: 5.00 },
  FS4:        { description: 'ReEnter/Install Enclosure', uom: 'EA', contractor_rate: 100.00 },
  FS05:       { description: 'Ribbon splice', uom: 'EA', contractor_rate: 13.00 },
  UG1:        { description: 'Directional bore 1x 1.25in subduct', uom: 'LF', contractor_rate: 6.00 },
  UG2:        { description: 'Directional bore 2x 1.25in subduct', uom: 'LF', contractor_rate: 6.00 },
  UG3:        { description: 'Directional bore 3x 1.25in subduct', uom: 'LF', contractor_rate: 6.00 },
  UG16:       { description: 'Directional bore 4x 1.25in subduct', uom: 'LF', contractor_rate: 6.50 },
  UG23:       { description: 'Directional bore 5x 1.25in subduct', uom: 'LF', contractor_rate: 7.00 },
  UG24:       { description: 'Directional bore 6x 1.25in subduct', uom: 'LF', contractor_rate: 8.00 },
  UG21:       { description: '4in HDPE Duct Install bore', uom: 'LF', contractor_rate: 6.00 },
  UG29:       { description: 'Bore 1x 2in duct', uom: 'LF', contractor_rate: 6.00 },
  UG30:       { description: 'Bore 2x 2in duct', uom: 'LF', contractor_rate: 6.00 },
  UG32:       { description: 'Cut/bore/saw', uom: 'LF', contractor_rate: 6.00 },
  UG4:        { description: 'Pull up to 144ct armored/all micro cable in duct', uom: 'LF', contractor_rate: 0.40 },
  UG22:       { description: 'Pull inner duct', uom: 'LF', contractor_rate: 0.45 },
  UG28:       { description: 'Pull 288-432ct armored fiber in duct', uom: 'LF', contractor_rate: 0.60 },
  UG5:        { description: 'Direct Bury Cable - Plow', uom: 'LF', contractor_rate: 1.50 },
  UG6:        { description: 'Direct Bury Cable add depth 6in increments', uom: 'EA', contractor_rate: 0.40 },
  UG7:        { description: 'Direct Bury Pipe - Plow', uom: 'LF', contractor_rate: 1.60 },
  UG8:        { description: 'Direct Bury Pipe add duct', uom: 'LF', contractor_rate: 0.40 },
  UG9:        { description: 'Buried plant Pedestal', uom: 'EA', contractor_rate: 30.00 },
  UG10:       { description: 'Fiberglass/polycrete Handhole 30x48x30', uom: 'EA', contractor_rate: 240.00 },
  UG11:       { description: 'Fiberglass/polycrete Handhole 24x36x24', uom: 'EA', contractor_rate: 85.00 },
  UG12:       { description: 'Utility Box / Flowerpot', uom: 'EA', contractor_rate: 16.00 },
  UG13:       { description: 'Ground rod 5/8in x 8ft', uom: 'EA', contractor_rate: 30.00 },
  UG14:       { description: 'Locate Marker post / Aux Ground Assembly', uom: 'EA', contractor_rate: 14.00 },
  UG15:       { description: 'Route Marker Post', uom: 'EA', contractor_rate: 9.00 },
  UG17:       { description: 'HDPE Handhole 17x30x18', uom: 'EA', contractor_rate: 48.00 },
  UG18:       { description: 'HDPE Handhole 24x36x18', uom: 'EA', contractor_rate: 100.00 },
  UG19:       { description: 'HDPE Handhole 30x48x18', uom: 'EA', contractor_rate: 200.00 },
  UG20:       { description: 'Terminal Box', uom: 'EA', contractor_rate: 30.00 },
  UG27:       { description: 'HDPE Handhole 30x48x24', uom: 'EA', contractor_rate: 160.00 },
  UG31:       { description: 'Ground rod clamp/wire into marker post', uom: 'EA', contractor_rate: 12.00 },
  PP1:        { description: 'Place Pole 35ft Class 7', uom: 'EA', contractor_rate: 280.00 },
  PP2:        { description: 'Hand Carry/Set in rear Easement', uom: 'EA', contractor_rate: 80.00 },
  PP3:        { description: 'Detach and Remove Pole up to 35ft', uom: 'EA', contractor_rate: 160.00 },
  BCP:        { description: 'Pole Banding', uom: 'EA', contractor_rate: 0.00 },
  PA01:       { description: 'Place Asphalt up to 4in', uom: 'SF', contractor_rate: 15.00 },
  PA02:       { description: 'Place Asphalt over 4in up to 8in', uom: 'SF', contractor_rate: 24.00 },
  PA02A:      { description: 'Place Asphalt over 8in depth additive', uom: 'SF', contractor_rate: 2.25 },
  PC01:       { description: 'Place Concrete up to 4in', uom: 'SF', contractor_rate: 20.00 },
  PC02:       { description: 'Place Concrete over 4in up to 8in', uom: 'SF', contractor_rate: 30.00 },
  PC02A:      { description: 'Place Concrete over 8in depth additive', uom: 'SF', contractor_rate: 3.00 },
  RA1:        { description: 'Remove Asphalt', uom: 'CF', contractor_rate: 15.00 },
  RC1:        { description: 'Remove Concrete', uom: 'CF', contractor_rate: 30.00 },
  HSPH:       { description: 'Hardscape Potholing', uom: 'EA', contractor_rate: 150.00 },
  TC1:        { description: 'Traffic control personnel', uom: 'HR', contractor_rate: 30.00 },
  L10A:       { description: 'Foreman', uom: 'HR', contractor_rate: 35.00 },
  L30A:       { description: 'Lineman', uom: 'HR', contractor_rate: 30.00 },
  L40A:       { description: 'Technician', uom: 'HR', contractor_rate: 30.00 },
  L50A:       { description: 'Laborer', uom: 'HR', contractor_rate: 25.00 },
  L70A:       { description: 'Supervisor', uom: 'HR', contractor_rate: 55.00 },
  E10:        { description: 'Pickup truck', uom: 'HR', contractor_rate: 7.00 },
  E20:        { description: 'Directional drill', uom: 'HR', contractor_rate: 90.00 },
  E30:        { description: 'Mini excavator', uom: 'HR', contractor_rate: 35.00 },
  E40:        { description: 'Backhoe', uom: 'HR', contractor_rate: 42.00 },
  E50:        { description: 'Trailer', uom: 'HR', contractor_rate: 9.00 },
  E60:        { description: 'Air compressor', uom: 'HR', contractor_rate: 14.00 },
  E70:        { description: 'Generator', uom: 'HR', contractor_rate: 12.00 },
  E80:        { description: 'Cable reel trailer', uom: 'HR', contractor_rate: 12.00 },
  E82:        { description: 'Fusion splicer', uom: 'HR', contractor_rate: 20.00 },
};

export const HIGHLINE_RATES = {
  'HL-UG02':  { description: 'Composite UG conduit placement up to 2" (bore+tracer+pothole)', uom: 'LF', contractor_rate: 4.88 },
  'HL-UG04':  { description: 'Composite UG conduit placement up to 4" hole (3x1.25" + HH)', uom: 'LF', contractor_rate: 5.25 },
  'HL-UG06':  { description: 'Bore up to 2" w/ tracer, 36" cover', uom: 'LF', contractor_rate: 4.25 },
  'HL-UG08':  { description: 'Bore up to 2" w/ tracer, 48" cover', uom: 'LF', contractor_rate: 4.37 },
  'HL-UG10':  { description: 'Bore up to 3x2" w/ tracer, 36" cover', uom: 'LF', contractor_rate: 5.63 },
  'HL-UG12':  { description: 'Bore up to 3x2" w/ tracer, 48" cover', uom: 'LF', contractor_rate: 6.00 },
  'HL-UG14':  { description: 'Bore up to 4" w/ tracer, 36" cover', uom: 'LF', contractor_rate: 6.38 },
  'HL-UG16':  { description: 'Bore up to 4" w/ tracer, 48" cover', uom: 'LF', contractor_rate: 6.56 },
  'HL-UG18':  { description: 'Additional conduit adder up to 2", 36" cover', uom: 'LF', contractor_rate: 0.45 },
  'HL-UG20':  { description: 'Additional conduit adder up to 2", 48" cover', uom: 'LF', contractor_rate: 0.56 },
  'HL-UG22':  { description: 'Additional conduit adder up to 4", 36" cover', uom: 'LF', contractor_rate: 0.75 },
  'HL-UG24':  { description: 'Additional conduit adder up to 4", 48" cover', uom: 'LF', contractor_rate: 0.94 },
  'HL-UG26':  { description: 'Soft rock/cobble adder, 36" cover', uom: 'LF', contractor_rate: 9.75 },
  'HL-UG28':  { description: 'Soft rock/cobble adder, 48" cover', uom: 'LF', contractor_rate: 12.75 },
  'HL-UG30':  { description: 'Solid rock adder, 36" cover', uom: 'LF', contractor_rate: 21.00 },
  'HL-UG32':  { description: 'Solid rock adder, 48" cover', uom: 'LF', contractor_rate: 22.50 },
  'HL-UG34':  { description: 'Plow 36" depth, 1 duct', uom: 'LF', contractor_rate: 1.69 },
  'HL-UG36':  { description: 'Plow 48" depth, 1 duct', uom: 'LF', contractor_rate: 2.25 },
  'HL-UG38':  { description: 'Pre-rip, after 2 rips', uom: 'LF', contractor_rate: 0.45 },
  'HL-UG40':  { description: 'Plow 36" depth, 2nd duct adder', uom: 'LF', contractor_rate: 0.15 },
  'HL-UG42':  { description: 'Plow 48" depth, 2nd duct adder', uom: 'LF', contractor_rate: 0.26 },
  'HL-UG44':  { description: 'Plow 12" depth, direct bury cable/micro duct', uom: 'LF', contractor_rate: 0.75 },
  'HL-UG46':  { description: 'Open trench chain cut, 36" cover', uom: 'LF', contractor_rate: 3.19 },
  'HL-UG48':  { description: 'Open trench chain cut, 48" cover', uom: 'LF', contractor_rate: 3.38 },
  'HL-UG50':  { description: 'Open trench mini excavator, 36" cover', uom: 'LF', contractor_rate: 3.19 },
  'HL-UG52':  { description: 'Open trench mini excavator, 48" cover', uom: 'LF', contractor_rate: 3.75 },
  'HL-UG58':  { description: 'Rock saw 2" wide, 24" cover', uom: 'LF', contractor_rate: 30.00 },
  'HL-UG60':  { description: 'Rock saw 4" wide, 24" cover', uom: 'LF', contractor_rate: 31.50 },
  'HL-UG62':  { description: 'Joint trench, up to two 2" ducts, 36" cover', uom: 'LF', contractor_rate: 0.75 },
  'HL-UG64':  { description: 'Joint trench, up to two 2" ducts, 48" cover', uom: 'LF', contractor_rate: 0.75 },
  'HL-UG66':  { description: 'Micro-trench, 3/4" wide x 4"-6" deep', uom: 'LF', contractor_rate: 5.24 },
  'HL-UG68':  { description: 'Micro-trench, diamond blade gravel roads 20" depth', uom: 'LF', contractor_rate: 8.51 },
  'HL-UG70':  { description: 'Hand dig trench up to 50\', 24"', uom: 'EA', contractor_rate: 240.00 },
  'HL-UG72':  { description: 'Duct rod/locate/rope existing duct', uom: 'LF', contractor_rate: 0.49 },
  'HL-UG74':  { description: 'Place cable in conduit, up to 288F (blow/pull)', uom: 'SF', contractor_rate: 0.49 },
  'HL-UG76':  { description: 'Additional cable in conduit adder, up to 288F', uom: 'SF', contractor_rate: 0.30 },
  'HL-UG78':  { description: 'Install 144F micro FDH combo pedestal/HH w/ cassettes', uom: 'EA', contractor_rate: 131.25 },
  'HL-UG80':  { description: 'Install vault 30x48x36 concrete w/ pea gravel+GR', uom: 'EA', contractor_rate: 150.00 },
  'HL-UG82':  { description: 'Install vault 24x36x24 concrete w/ pea gravel+GR', uom: 'EA', contractor_rate: 123.75 },
  'HL-UG84':  { description: 'Install vault 17x30x24 concrete w/ pea gravel+GR', uom: 'EA', contractor_rate: 112.50 },
  'HL-UG86':  { description: 'Install vault 13x24x18 concrete w/ pea gravel+GR', uom: 'EA', contractor_rate: 86.25 },
  'HL-UG88':  { description: 'Install HH 30x48x36 HDPE w/ pea gravel+GR', uom: 'EA', contractor_rate: 96.00 },
  'HL-UG90':  { description: 'Install HH 24x36x30 HDPE w/ pea gravel+GR', uom: 'EA', contractor_rate: 75.00 },
  'HL-UG92':  { description: 'Install HH 17x30x15 HDPE w/ pea gravel+GR', uom: 'EA', contractor_rate: 60.00 },
  'HL-UG94':  { description: 'Install HH 10x16x12 HDPE JD2001 SB style', uom: 'EA', contractor_rate: 20.25 },
  'HL-UG96':  { description: 'Install HH 10x15x12 HDPE JD2000 SB style', uom: 'EA', contractor_rate: 20.25 },
  'HL-UG98':  { description: 'Install BDO dog house style pedestal', uom: 'EA', contractor_rate: 93.75 },
  'HL-UG100': { description: 'Install sprinkler valve box/Toby box for ABF', uom: 'EA', contractor_rate: 12.75 },
  'HL-UG102': { description: 'Install HH 12x15x12 flowerpot style', uom: 'EA', contractor_rate: 16.13 },
  'HL-UG104': { description: 'Install 10" round JD1000 flowerpot', uom: 'EA', contractor_rate: 12.00 },
  'HL-UG106': { description: 'Install round marker HDPE w/ bonding', uom: 'EA', contractor_rate: 5.25 },
  'HL-UG108': { description: 'Install flat marker stake, fiberglass', uom: 'EA', contractor_rate: 5.25 },
  'HL-UG110': { description: 'Install round marker post w/ locate wire to vault', uom: 'EA', contractor_rate: 5.81 },
  'HL-UG112': { description: 'Install pedestal 8" round/square w/ GR', uom: 'EA', contractor_rate: 15.00 },
  'HL-UG114': { description: 'Install pedestal 10" round/square w/ GR', uom: 'EA', contractor_rate: 22.50 },
  'HL-UG116': { description: 'Install pedestal 12" round/square w/ GR', uom: 'EA', contractor_rate: 24.00 },
  'HL-UG118': { description: 'Dig/swing existing cable, set new 24x36x24 vault', uom: 'EA', contractor_rate: 176.25 },
  'HL-UG120': { description: 'Dig/swing existing cable, set new 17x30x24 vault', uom: 'EA', contractor_rate: 135.00 },
  'HL-UG122': { description: 'Dig & swing existing fiber or duct', uom: 'EA', contractor_rate: 67.50 },
  'HL-AE02':  { description: 'Composite aerial strand placement (strand+guys+anchors+bond)', uom: 'LF', contractor_rate: 0.53 },
  'HL-AE03':  { description: 'Composite aerial strand + up to 2x288F cable', uom: 'LF', contractor_rate: 0.90 },
  'HL-AE04':  { description: 'Placement of 1/4" strand and pole hardware only', uom: 'LF', contractor_rate: 0.41 },
  'HL-AE06':  { description: 'Install/lash up to 2 fiber cables to existing strand', uom: 'SF', contractor_rate: 0.53 },
  'HL-AE08':  { description: 'Install/lash additional (3rd) cable adder', uom: 'SF', contractor_rate: 0.15 },
  'HL-AE10':  { description: 'Install fiber on strand, rear easement adder', uom: 'SF', contractor_rate: 0.07 },
  'HL-AE12':  { description: 'Overlash fiber on existing cable', uom: 'SF', contractor_rate: 0.45 },
  'HL-AE14':  { description: 'Overlash fiber, rear easement adder', uom: 'SF', contractor_rate: 0.10 },
  'HL-AE16':  { description: 'Delash and relash fiber', uom: 'SF', contractor_rate: 0.41 },
  'HL-AE18':  { description: 'Raise/lower existing attachment (simple)', uom: 'EA', contractor_rate: 45.00 },
  'HL-AE20':  { description: 'Raise/lower existing attachment (complex)', uom: 'EA', contractor_rate: 67.50 },
  'HL-AE22':  { description: 'Removal of all existing strand, cables, pole hardware', uom: 'EA', contractor_rate: 0.45 },
  'HL-AE24':  { description: 'Top pole', uom: 'EA', contractor_rate: 61.50 },
  'HL-AE26':  { description: 'Install snowshoes (2) and slack loop', uom: 'EA', contractor_rate: 45.00 },
  'HL-AE28':  { description: 'Install screw anchor', uom: 'EA', contractor_rate: 35.25 },
  'HL-AE30':  { description: 'Install rock anchor', uom: 'EA', contractor_rate: 85.50 },
  'HL-AE32':  { description: 'Install down guy with guy guard', uom: 'EA', contractor_rate: 10.50 },
  'HL-AE34':  { description: 'Install riser guard', uom: 'EA', contractor_rate: 15.00 },
  'HL-AE36':  { description: 'Relocate pole attachment, straight inline', uom: 'EA', contractor_rate: 54.75 },
  'HL-AE38':  { description: 'Relocate pole attachment, single dead-end', uom: 'EA', contractor_rate: 60.00 },
  'HL-AE40':  { description: 'Relocate pole attachment, double dead-end', uom: 'EA', contractor_rate: 78.75 },
  'HL-AE42':  { description: 'Relocate pole attachment, down guy', uom: 'EA', contractor_rate: 44.25 },
  'HL-MRE02': { description: 'Install new pole up to 45ft in ROW', uom: 'EA', contractor_rate: 637.50 },
  'HL-MRE04': { description: 'Install new pole up to 45ft in ROW (rock)', uom: 'EA', contractor_rate: 750.00 },
  'HL-SPL02': { description: 'Fiber splice single + PM test + OTDR trace', uom: 'EA', contractor_rate: 11.25 },
  'HL-SPL04': { description: 'Fiber splice 12F ribbon + PM + OTDR', uom: 'EA', contractor_rate: 71.25 },
  'HL-SPL06': { description: 'Fiber test/OTDR trace (no splice)', uom: 'EA', contractor_rate: 2.25 },
  'HL-SPL08': { description: 'Fiber splicing labor hourly', uom: 'HR', contractor_rate: 41.25 },
  'HL-SPL10': { description: 'Prep and install fiber into splice closure + bonds', uom: 'EA', contractor_rate: 56.25 },
  'HL-SPYJU': { description: 'Prep/install fiber in FAT (CommScope OFDC) UG + bonds', uom: 'EA', contractor_rate: 78.00 },
  'HL-SPYJA': { description: 'Prep/install fiber in FAT (CommScope OFDC) aerial + bonds', uom: 'EA', contractor_rate: 82.50 },
  'HL-SPE02': { description: 'Prep/install fiber into pedestal (up to 12x12) + bonds', uom: 'EA', contractor_rate: 60.75 },
  'HL-SPE04': { description: 'Install electronics and power up active cabinet', uom: 'EA', contractor_rate: 120.00 },
  'HL-SPE06': { description: 'Install/place passive splitter cabinet (288-576F)', uom: 'EA', contractor_rate: 225.00 },
  'HL-HS02':  { description: 'Cut, remove & replace concrete', uom: 'SF', contractor_rate: 14.25 },
  'HL-HS04':  { description: 'Cut, remove & replace asphalt', uom: 'SF', contractor_rate: 12.75 },
  'HL-LAB2':  { description: 'Install ground rod w/ wire and clamp', uom: 'EA', contractor_rate: 6.00 },
  'HL-LAB4':  { description: 'Install concrete pad + large active cabinet', uom: 'EA', contractor_rate: 600.00 },
  'HL-LAB6':  { description: 'Install concrete pad + small/mini active cabinet', uom: 'EA', contractor_rate: 468.75 },
  'HL-LAB8':  { description: 'Install concrete pad + passive splitter cabinet', uom: 'EA', contractor_rate: 405.00 },
  'HL-LAB10': { description: 'Install H-frame for mini OLT', uom: 'EA', contractor_rate: 86.25 },
  'HL-LAB12': { description: 'Core drill building up to 4"', uom: 'EA', contractor_rate: 48.75 },
  'HL-TREE':  { description: 'Tree trimming labor (w/ disposal)', uom: 'LF', contractor_rate: 0.56 },
  'HL-EM':    { description: 'Emergency mobilization (per event)', uom: 'EA', contractor_rate: 262.50 },
};

export const IES_RATES = {
  IC1:        { description: 'Interior fiber cable pull (per foot)', uom: 'LF', contractor_rate: 1.80 },
  IC2:        { description: 'Fiber termination (per strand)', uom: 'EA', contractor_rate: 9.00 },
  IC3:        { description: 'Cable tray installation', uom: 'LF', contractor_rate: 6.00 },
  IC4:        { description: 'Patch panel installation', uom: 'EA', contractor_rate: 110.00 },
  IC5:        { description: 'OTDR testing (per fiber)', uom: 'EA', contractor_rate: 4.50 },
  L10A:       { description: 'Foreman', uom: 'HR', contractor_rate: 45.00 },
  L40A:       { description: 'Technician', uom: 'HR', contractor_rate: 35.00 },
};

// --- Rate resolution helpers ---

// Resolves a rate card id to the CONTRACTOR rate set (bundle-safe).
// For client billing rates, use fetchFullRates() in mapService.js (server-side, key-gated).
export function getRateInfo(rateCardId) {
  if (rateCardId && (rateCardId.includes('highline') || rateCardId.includes('aspen-bastrop')))
    return { rates: HIGHLINE_RATES, priceField: 'contractor_rate' };
  if (rateCardId && rateCardId.includes('ies'))
    return { rates: IES_RATES, priceField: 'contractor_rate' };
  return { rates: VEXUS_RATES, priceField: 'contractor_rate' };
}

export function getBoringCode(rateCardId, ductCount, boreCode, coverDepth) {
  if (rateCardId?.includes('highline') || rateCardId?.includes('aspen-bastrop')) {
    const is48 = coverDepth === '48';
    if (boreCode === 'plow') return is48 ? 'HL-UG36' : 'HL-UG34';
    if (boreCode === 'trench') return is48 ? 'HL-UG48' : 'HL-UG46';
    if (ductCount >= 3) return is48 ? 'HL-UG16' : 'HL-UG14';
    if (ductCount >= 2) return is48 ? 'HL-UG12' : 'HL-UG10';
    return is48 ? 'HL-UG08' : 'HL-UG06';
  }
  if (ductCount >= 6) return 'UG24';
  if (ductCount >= 5) return 'UG23';
  return 'UG1';
}

export function getPullingCode(rateCardId, fiberCount) {
  if (rateCardId?.includes('highline') || rateCardId?.includes('aspen-bastrop'))
    return 'HL-UG74';
  return fiberCount > 144 ? 'UG28' : 'UG4';
}

export function getSpliceCodeSet(rateCardId) {
  if (rateCardId?.includes('highline') || rateCardId?.includes('aspen-bastrop'))
    return { spliceCode: 'HL-SPL02', closureCode: 'HL-SPL10', ringCutCode: 'HL-SPL10', testCode: 'HL-SPL06' };
  return { spliceCode: 'FS1', closureCode: 'FS4', ringCutCode: 'FS2', testCode: 'FS3' };
}

// Default map preset configurations (fallback when no DB presets exist)
export const DEFAULT_MAP_PRESETS = {
  construction: {
    phase: 'boring',
    filterStatus: 'all',
    lytOnly: true,
    showHandholes: false,
    showFlowerpots: false,
    showSplicePoints: false,
    showPedestals: false,
    showCabinets: false,
    showFats: false,
    showAerial: false,
    showNotes: false,
  },
  pulling: {
    phase: 'pulling',
    filterStatus: 'all',
    lytOnly: true,
    showHandholes: true,
    showFlowerpots: true,
    showSplicePoints: false,
    showPedestals: false,
    showCabinets: false,
    showFats: false,
    showAerial: false,
    showNotes: false,
  },
  splicing: {
    phase: 'splicing',
    filterStatus: 'all',
    lytOnly: true,
    showHandholes: true,
    showFlowerpots: false,
    showSplicePoints: true,
    showPedestals: false,
    showCabinets: false,
    showFats: true,
    showAerial: false,
    showNotes: false,
  },
};

// v1.2.0 - Added PM thresholds, updated photo requirements with PM reading counts
// v1.3.0 - Added VEXUS_RATES master rate card with dual pricing
// v1.4.0 - Added HIGHLINE_RATES and IES_RATES placeholders for multi-client support
// v1.5.0 - Added getRateInfo, getBoringCode, getPullingCode, getSpliceCodeSet for dynamic rate card selection
// v1.6.0 - SECURITY: client rates removed from bundle (server-side only via /api/rates); rate objects are contractor-only
